import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { passwordChangeActions, passwordChangeSelectors } from '../reducer/password-change-reducer';
import { lionSelectors, LionTranslate } from '@rosetta/react-lion';
import styled from '@emotion/styled';
import { LaunchpadButtonNew, Link } from '../../../styles/LaunchpadButton';
import { workspaceInputAlertColor, black } from '../../../styles/colors';
import greenCheck from '../../../assets/images/check-solid-green.svg';
import passwordPolicyCheckGreen from '../../../assets/images/password-policy-check-green.svg';
import passwordErrorRed from '../../../assets/images/password-policy-error-red.svg';
import { MouseCatcherContainer as MouseCatcher } from '../../app/util/MouseCatcher';
import { appActions, appSelectors, TRANSACTION_STATE } from '../../app/reducer/app-reducer';
import { validateUserInfo } from '../../app/util/user-helper';
import { withWindowDimensions } from '@rosetta/react-common-ui';
import { LoginContainer } from './LoginContainer'
import { TextInput } from '../../app/util/TextInput';


const Form = styled.form`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  & > *:not(:last-child) {
    margin-bottom: 12px;
  }
`

const ResetPasswordConfirmationHeading = styled.div({
  fontSize: '30px',
  fontWeight: '150',
  letterSpacing: '1px',
  lineHeight: '1.1',
  margin: '0',
  paddingBottom: '20px',
  textAlign: 'center'
});

const ResetPasswordConfirmation = styled.div(() => {
  const iconSize = 36;
  let style = {
    backgroundImage: 'url(' + greenCheck + ')',
    backgroundRepeat: 'no-repeat',
    height: iconSize,
    width: iconSize,
    display: 'block',
    margin: 'auto'
  };
  return style;
});

const ResetPasswordConfirmationInstructions = styled.div({
  fontSize: '18px',
  fontWeight: '400',
  lineHeight: '22px',
  paddingTop: '20px',
  textAlign: 'center'
});

const ValidationMessage = styled.div`
  color: ${workspaceInputAlertColor};
  text-align: left;
  margin: 0 auto 7px auto;
`

const Li = styled.li((props) => {
  return {
    color: black,
    margin: 0,
    padding: 0,
    paddingLeft: '17px',
    listStyleType: 'none',
    background: `url(${props.icon}) left center no-repeat`,
  }
});


export class PasswordReset extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      password: '',
      passwordConfirm: '',
      validatingPassword: false,
      validatingPasswordMatch: false
    };

    // the following is probably only because of dev situations - if you get here by pasting
    //  the url in after failing at login or at password request, we need to clear the decks
    props.setLastTransactionState(TRANSACTION_STATE.NO_TRANSACTION);

    // ask ego what the rules are about creating passwords in this org
    props.loadPasswordMode({ userId: undefined, resetPasswordId: this.props.match.params.reset_uuid });
  }

  textInput = (event, firstPasswordPrompt) => {
    if (firstPasswordPrompt) {
      this.setState({ password: event.target.value });
    } else {
      this.setState({ passwordConfirm: event.target.value });
    }
  };

  componentDidMount() {
    // Clear any error messages
    this.props.errorKeyUpdated(null)
  }

  passwordError = () => {
    // Instead of using the passwordMode return from ego, we hardcode to 'strict-new'
    // because currently no org passwordPolicy variable exists in the Ego database, so
    // the ego api always returns "false", which is interpreted as "simple"
    return validateUserInfo('newPassword', this.state.password, '', 'strict-new');
  };

  passwordMatchError = () => {
    return validateUserInfo(
      'newPasswordMatch',
      this.state.passwordConfirm,
      this.state.password,
      this.props.passwordMode
    );
  };

  isSubmitDisabled = () => {
    return (
      this.props.lastTransactionState !== TRANSACTION_STATE.NO_TRANSACTION
      || this.passwordError()
      || this.passwordMatchError()
    );
  };

  submitChange = e => {
    e.preventDefault();
    if (!this.isSubmitDisabled()) {
      const params = new URLSearchParams(this.props.location.search);
      this.props.processPasswordReset(this.props.match.params.reset_uuid, this.state.password, this.state.passwordConfirm, params.get('userType'), params.get('key'));
    }
  };

  // the password is only validated after the user clicks outside the input, the first time,
  //  and only if there is something in the field
  //  after the first time they type something in the match then blur, it always validates
  //  even if the field becomes empty - but not the first time if it is empty
  handlePasswordBlur = () => {
    if (this.state.password.length > 0) {
      this.setState({ validatingPassword: true });
    }
  };

  // on this page the confirm password acts the same as the password, in terms of when to validate
  handlePasswordMatchBlur = () => {
    if (this.state.passwordConfirm.length > 0) {
      this.setState({ validatingPasswordMatch: true });
    }
  };

  returnToLogin = () => {
    this.props.logoutUser();
  };

  render = () => {

    if (!this.props.lionLoaded) {
      return <MouseCatcher />;
    }

    if (!this.props.passwordMode) {
      return <MouseCatcher />;
    }

    // this page shows success for a few seconds before whisking the user back to login
    if (this.props.lastTransactionState === TRANSACTION_STATE.SUCCESS) {
      return (
        <LoginContainer header={""} errorMessage={this.props.errorKey}>
          <ResetPasswordConfirmationHeading>
            <LionTranslate lionkey="resetpassword_heading" />
          </ResetPasswordConfirmationHeading>
          <ResetPasswordConfirmation />
          <ResetPasswordConfirmationInstructions>
            <LionTranslate lionkey="profile_password_change_success" />
          </ResetPasswordConfirmationInstructions>
        </LoginContainer>
      );
    }

    const localizedPasswordPrompt = this.props.getLionTranslatedString('profile_newpassword_label');
    const localizedPasswordConfirmPrompt = this.props.getLionTranslatedString('confirm_password');

    let validationMessageBlock = null;
    let passwordHasError = false;
    if (this.state.validatingPassword) {
      let passwordErrorKey = this.passwordError();
      let isTooShort = true;
      let hasOneDigit = false;
      let hasOneUppercase = false;
      let hasOneLowercase = false;
      let hasOneSpecialChar = false;
      let hasIllegalChar = false;
      if (typeof passwordErrorKey !== 'string') {
        [isTooShort, hasOneDigit, hasOneUppercase, hasOneLowercase, hasOneSpecialChar, hasIllegalChar] = passwordErrorKey;
      }

      if (passwordErrorKey) {
        passwordHasError = true;
        validationMessageBlock = (
          <ValidationMessage data-qa="PasswordError">
            {/* {this.props.getLionTranslatedString(passwordErrorKey)} */}
            {this.props.getLionTranslatedString('password_requirements_header')}
              <Li icon={(hasOneUppercase && hasOneLowercase) ? passwordPolicyCheckGreen : passwordErrorRed}>
              {this.props.getLionTranslatedString('error_password_needs_upper_lower_case')}
              </Li>

              <Li icon={hasOneDigit ? passwordPolicyCheckGreen : passwordErrorRed}>
                {this.props.getLionTranslatedString('error_password_needs_number')}
              </Li>

              <Li icon={hasOneSpecialChar ? passwordPolicyCheckGreen : passwordErrorRed}>
                {this.props.getLionTranslatedString('error_password_needs_symbol')}
              </Li>

              <Li icon={hasIllegalChar ? passwordErrorRed : passwordPolicyCheckGreen}>
                {this.props.getLionTranslatedString('error_passwords_only_supported_chars')}
              </Li>

              <Li icon={isTooShort ? passwordErrorRed : passwordPolicyCheckGreen}>
                {this.props.getLionTranslatedString('error_password_is_too_short')}
              </Li>
          </ValidationMessage>
        );
      }
    }

    let confirmationMessageBlock = null;
    const passwordMatchHasError = this.state.validatingPasswordMatch && this.passwordMatchError().length > 0;
    if (passwordMatchHasError) {
      confirmationMessageBlock = (
        <ValidationMessage data-qa="PasswordMatchError">
          {this.props.getLionTranslatedString(this.passwordMatchError())}
        </ValidationMessage>
      );
    }

    //   show mousecatcher while request to reset is pending;
    //   continue showing mousecatcher on fail because we display error by
    //   going back to ForgotPassword and we should not redisplay here first
    let mouseCatcher = null;
    if (
      this.props.lastTransactionState === TRANSACTION_STATE.PENDING ||
      this.props.lastTransactionState === TRANSACTION_STATE.FAIL
    ) {
      mouseCatcher = <MouseCatcher />;
    }

    return (
      <LoginContainer header={<LionTranslate lionkey="resetpassword_heading" />} errorMessage={this.props.errorKey}>
          <Form>
            <TextInput
              type="password"
              autoFocus={true}
              placeholder={localizedPasswordPrompt}
              tabIndex={1}
              value={this.state.password}
              onBlur={this.handlePasswordBlur}
              onChange={(event) => this.textInput(event, true)}
              displayError={passwordHasError}
            />

            {validationMessageBlock}

            <TextInput
              type="password"
              placeholder={localizedPasswordConfirmPrompt}
              tabIndex={2}
              value={this.state.passwordConfirm}
              onBlur={this.handlePasswordMatchBlur}
              onChange={(event) => this.textInput(event, false)}
              displayError={passwordMatchHasError}
            />

            {confirmationMessageBlock}

            <LaunchpadButtonNew
              data-qa="SavePasswordButton"
              tabIndex={3}
              onClick={this.submitChange}
              disabled={this.isSubmitDisabled()}
            >
              {/* {this.props.getLionTranslatedString('resetpassword_button_changepassword')} */}
              {this.props.getLionTranslatedString('save_button')}
            </LaunchpadButtonNew>
          </Form>

          <Link data-qa="ResetPasswordCancelLink" onClick={() => this.returnToLogin()}>
            {this.props.getLionTranslatedString('forgotpassword_cancel_link')}
          </Link>
        {mouseCatcher}
      </LoginContainer>
    );
  };
}

const mapStateToProps = (state, ownprops) => ({
  lionLoaded: lionSelectors.getLionAppResourcesLoaded(state),
  getLionTranslatedString: (key) => lionSelectors.getLionTranslatedString(state, key),
  isInterfaceRtl: appSelectors.isInterfaceRtl(state),
  lastTransactionState: appSelectors.lastTransactionState(state),
  passwordMode: passwordChangeSelectors.getPasswordMode(state),
  errorKey: appSelectors.errorKey(state),
});

const wrapActions = {
  setLastTransactionState: appActions.setLastTransactionState,
  loadPasswordMode: passwordChangeActions.loadPasswordMode,
  processPasswordReset: passwordChangeActions.processPasswordReset,
  logoutUser: appActions.logoutUser,
  errorKeyUpdated: appActions.errorKeyUpdated,
};
export const PasswordResetContainer = connect(
  mapStateToProps,
  wrapActions
)(withWindowDimensions(PasswordReset));



// WEBPACK FOOTER //
// ./src/modules/login/components/PasswordReset.jsx